'use client';
/**
 * @file     FooterAppBadges.tsx
 * @readme   ./README.md
 * @purpose  "Get the Relta app" in the footer: the official App Store and Google Play badges, linked to the store listings, with a click event. CSS modules, so it renders inside the Emotion-free footer.
 */

import { useTranslation } from 'react-i18next';
import { useAnalytics } from '@/hooks/useAnalytics';
import { analyticsEvents } from '@/core/constants/analytics';
import { STORE_BADGES, drawWidth, type StoreBadgeId } from '@/components/prompts/storeBadges';
import styles from './Footer.module.css';

const ORDER: StoreBadgeId[] = ['app_store', 'google_play'];

export function FooterAppBadges({ compact = false }: { compact?: boolean } = {}) {
  const { t } = useTranslation();
  const { trackEvent } = useAnalytics();

  const onClick = (store: StoreBadgeId, href: string) => {
    trackEvent({
      eventName: analyticsEvents.appStoreBadgeClicked,
      eventType: 'click',
      category: 'outbound',
      label: store,
      metadata: { store, location: 'footer', destination: href },
    });
  };

  return (
    <div className={`${styles.appGroup} ${compact ? styles.appGroupCompact : ''}`}>
      {compact ? null : (
        <p className={styles.colTitle}>{t('footer.app.title', { defaultValue: 'Get the Relta app' })}</p>
      )}
      <div className={styles.appBadges}>
        {ORDER.map((id) => {
          const badge = STORE_BADGES[id];
          const alt = t(badge.altKey, { defaultValue: badge.altDefault });
          const width = drawWidth(badge);
          const padded = badge.src.endsWith('.png');
          return (
            <a
              key={id}
              href={badge.href}
              target="_blank"
              rel="noopener noreferrer"
              className={`${styles.appBadge} ${padded ? styles.appBadgePadded : ''}`}
              data-store={id}
              onClick={() => onClick(id, badge.href)}
            >
              {/* eslint-disable-next-line @next/next/no-img-element -- official artwork, served as is */}
              <img src={badge.src} alt={alt} width={width} height={badge.drawHeight} loading="lazy" decoding="async" className={badge.srcDark !== badge.src ? styles.appBadgeLight : undefined} />
              {badge.srcDark !== badge.src ? (
                // eslint-disable-next-line @next/next/no-img-element -- official artwork, served as is
                <img src={badge.srcDark} alt={alt} width={width} height={badge.drawHeight} loading="lazy" decoding="async" className={styles.appBadgeDark} />
              ) : null}
            </a>
          );
        })}
      </div>
    </div>
  );
}
